import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import {
  doGetDocumentsList,
  doCreateDocument,
  doDeleteDocument,
  doPatchDocumentName,
  luHandleAppiError,
  type U3142LotDetailDocument,
} from "../../U3142Service";
import { NS } from "../../U3142";

/** Handlers pour charger la liste des documents (GET documents). */
export function useU3142GetDocumentsHandlers(params: {
  lotId: string | number;
  onSuccess?: (list: U3142LotDetailDocument[]) => void;
}) {
  const { lotId, onSuccess } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const loadDocuments = useCallback(async () => {
    try {
      const list = await doGetDocumentsList(axios, lotId);
      onSuccess?.(list);
      return list;
    } catch (error: unknown) {
      const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
      return [];
    }
  }, [axios, lotId, onSuccess, t, toast]);

  return { loadDocuments };
}

/** Handlers pour créer un document (POST multipart). */
export function useU3142AddDocumentHandlers(params: { lotId: string | number; onSuccess?: () => void }) {
  const { lotId, onSuccess } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const createDocument = useCallback(
    async (formData: FormData) => {
      await doCreateDocument(axios, lotId, formData);
    },
    [axios, lotId],
  );

  const addDocument = useCallback(
    async (formData: FormData) => {
      try {
        await createDocument(formData);
        toast.success(t("msgDocumentAdded", { ns: NS }));
        onSuccess?.();
      } catch (error: unknown) {
        const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [createDocument, onSuccess, t, toast],
  );

  return { addDocument, createDocument };
}

/** Handlers pour supprimer un document (DELETE). */
export function useU3142DeleteDocumentHandlers(params: { lotId: string | number; onSuccess?: () => void }) {
  const { lotId, onSuccess } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const deleteDocument = useCallback(
    async (documentId: string | number) => {
      try {
        await doDeleteDocument(axios, lotId, documentId);
        toast.success(t("msgDocumentDeleted", { ns: NS }));
        onSuccess?.();
      } catch (error: unknown) {
        const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [axios, lotId, onSuccess, t, toast]
  );

  return { deleteDocument };
}

/** Handlers pour renommer un document (PATCH documentName). */
export function useU3142PatchDocumentNameHandlers(params: { lotId: string | number; onSuccess?: () => void }) {
  const { lotId, onSuccess } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const patchDocumentName = useCallback(
    async (documentId: string | number, documentName: string) => {
      try {
        await doPatchDocumentName(axios, lotId, documentId, documentName);
        toast.success(t("msgDocumentRenamed", { ns: NS }));
        onSuccess?.();
      } catch (error: unknown) {
        const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [axios, lotId, onSuccess, t, toast]
  );

  return { patchDocumentName };
}

/** Paramètres pour le hook viewDocument. */
export interface U3142UseViewDocumentHandlersParams {
  doViewDocumentFn: (documentId: string | number) => Promise<{ data?: { signUrl?: string; result?: { signUrl?: string } } }>;
  onSuccess?: (url: string) => void;
  onError?: (error: unknown) => void;
}

/** Handlers pour ouvrir un document (GET signUrl puis ouverture nouvel onglet). */
export function useU3142UseViewDocumentHandlers({
  doViewDocumentFn,
  onSuccess,
  onError,
}: U3142UseViewDocumentHandlersParams) {
  const viewDocument = useCallback(
    async (documentId: string | number) => {
      try {
        const response = await doViewDocumentFn(documentId);
        const data = response?.data;
        const signUrl = data?.signUrl ?? (data as { result?: { signUrl?: string } })?.result?.signUrl;
        if (signUrl) {
          onSuccess?.(signUrl);
          window.open(signUrl, "_blank", "noopener,noreferrer");
        }
      } catch (error) {
        onError?.(error);
      }
    },
    [doViewDocumentFn, onSuccess, onError]
  );

  return { viewDocument };
}
